import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";

export type UiTone = "neutral" | "brand" | "info" | "success" | "warning" | "danger";

const toneClasses: Record<UiTone, string> = {
  neutral: "border-white/12 bg-surface-1 text-foreground",
  brand: "border-primary/28 bg-primary/8 text-primary-soft",
  info: "border-accent/28 bg-accent/8 text-accent-soft",
  success: "border-success/28 bg-success/8 text-success-soft",
  warning: "border-warning/28 bg-warning/8 text-warning-soft",
  danger: "border-destructive/28 bg-destructive/8 text-destructive-soft",
};

export function Panel({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("surface-card p-5 sm:p-6", className)} {...props} />;
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  className,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <header className={cn("app-page-header", className)}>
      <div className="max-w-3xl">
        {eyebrow ? <div className="eyebrow">{eyebrow}</div> : null}
        <h1 className="mt-2 text-3xl font-semibold tracking-[-0.045em] text-white sm:text-4xl">
          {title}
        </h1>
        {description ? (
          <div className="mt-3 max-w-[70ch] text-sm leading-6 text-fg-secondary sm:text-base">
            {description}
          </div>
        ) : null}
      </div>
      {actions ? <div className="flex flex-wrap gap-3">{actions}</div> : null}
    </header>
  );
}

export function StatusBanner({
  tone = "neutral",
  title,
  children,
  className,
  role = "status",
}: {
  tone?: UiTone;
  title?: ReactNode;
  children: ReactNode;
  className?: string;
  role?: "status" | "alert";
}) {
  return (
    <div
      role={role}
      aria-live={role === "alert" ? "assertive" : "polite"}
      className={cn("rounded-2xl border px-4 py-3 text-sm leading-6", toneClasses[tone], className)}
    >
      {title ? <div className="font-semibold text-white">{title}</div> : null}
      <div className={title ? "mt-1" : undefined}>{children}</div>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("surface-card px-5 py-10 text-center sm:px-8", className)}>
      <h2 className="text-xl font-semibold text-white">{title}</h2>
      {description ? <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-fg-secondary">{description}</p> : null}
      {action ? <div className="mt-5 flex justify-center">{action}</div> : null}
    </div>
  );
}
